import { useEffect } from 'react';
import { create } from 'zustand';

/* `public/sw.js` is hand-rolled (no vite-plugin-pwa), and its `install` handler already calls
   `skipWaiting()` unconditionally — a new worker activates and claims clients the moment it's
   fetched, no "waiting" phase to hook a prompt into. That's staying as-is (see sw.js's own
   comment); what's missing is telling the OPEN tab that its already-loaded JS is now stale.

   `controllerchange` is the right signal for exactly that: per spec it fires only when the
   active service worker controlling this page actually changes while the page is open — never
   on the very first load (there's no prior controller to change from). One firing == one real
   deploy landed under the user's feet. */

interface UpdatePromptState {
  needsReload: boolean;
  markNeedsReload: () => void;
  dismiss: () => void;
}

export const useUpdatePromptStore = create<UpdatePromptState>((set) => ({
  needsReload: false,
  markNeedsReload: () => set({ needsReload: true }),
  dismiss: () => set({ needsReload: false }),
}));

type ControllerChangeTarget = Pick<ServiceWorkerContainer, 'addEventListener' | 'removeEventListener'>;

/** Exported standalone (no React) so the actual wiring is unit-testable with a fake
    EventTarget — jsdom doesn't implement navigator.serviceWorker at all. */
export function watchForServiceWorkerUpdate(
  container: ControllerChangeTarget | undefined,
  onUpdate: () => void,
): () => void {
  if (!container) return () => {};
  container.addEventListener('controllerchange', onUpdate);
  return () => container.removeEventListener('controllerchange', onUpdate);
}

export function useServiceWorkerUpdate(): void {
  const markNeedsReload = useUpdatePromptStore((s) => s.markNeedsReload);
  useEffect(() => {
    const container = 'serviceWorker' in navigator ? navigator.serviceWorker : undefined;
    return watchForServiceWorkerUpdate(container, markNeedsReload);
  }, [markNeedsReload]);
}
